import {
  Box,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { CustomDrawer, FormFooter, FormTextField } from "@shared/components";
import { Form, Formik } from "formik";
import { useMemo } from "react";
import {
  PartnerFormFields,
  PartnerFormInitialValues,
  PartnerValidationSchema,
  programOptions,
} from "../utils/constants";
import { AddUpdatePartnerFormProps, PartnersFormValues } from "../utils/types";
import { useGetAllPublishers } from "@modules/publishers/services/useGetAllPublishers";
import { useGetAllInvoiceParties } from "@modules/invoice-parties/services/useGetInvoiceParties";
import { useGetAllPartnerChannels } from "@modules/partner-channel/services/useGetAllPartnerChannels";
import { useGetAllTitleConfigurationsList } from "@modules/title-configuration/services/useGetTitleConfiguration";

const PLATFORM_OPTIONS = [
  "Android App",
  "Huawei App",
  "Android Web",
  "Desktop",
  "iOS App",
  "iOS Web",
  "Steam",
  "Windows App",
  "Other",
];

const DEVICE_OPTIONS = ["Desktop", "Phone", "Tablet", "Other"];

// Strip spaces, hyphens, underscores and lowercase for fuzzy option matching
const normalizeOption = (s: string) => s.toLowerCase().replace(/[\s\-_]+/g, "");

const PartnerForm = ({
  open,
  onClose,
  title,
  showCloseButton,
  initialValues,
  onSubmit,
  disable,
}: AddUpdatePartnerFormProps) => {
  
  
  const { data: partnerChannelsResp } = useGetAllPartnerChannels({
    data: {
      pageNumber: "1",
      pageSize: "1000",
      includeInactive: true,
    },
  });

  const partnerChannels = useMemo(
    () => partnerChannelsResp?.data || [],
    [partnerChannelsResp],
  );
  

  const mergedInitialValues = useMemo(() => {
    const merged = {
      ...PartnerFormInitialValues,
      ...initialValues,
    } as PartnersFormValues;

    // Normalize platform: match API values (e.g. "android-app") to option labels ("Android App")
    const platformStr = String(
      (merged as unknown as Record<string, unknown>).platform ?? "",
    );
    merged.platform =
      PLATFORM_OPTIONS.find(
        (opt) => normalizeOption(opt) === normalizeOption(platformStr),
      ) ?? (platformStr && platformStr !== "0" ? platformStr : "");

    // Normalize device: same approach
    const deviceStr = String(
      (merged as unknown as Record<string, unknown>).device ?? "",
    );
    merged.device =
      DEVICE_OPTIONS.find(
        (opt) => normalizeOption(opt) === normalizeOption(deviceStr),
      ) ?? (deviceStr && deviceStr !== "0" ? deviceStr : "");

    // Normalize partnerChannel: API may return the channelId (number) or string, keep as string
    const rawChannel = (merged as unknown as Record<string, unknown>)
      .partnerChannel;
    if (rawChannel !== undefined && rawChannel !== "" && rawChannel !== "0") {
      const asNum = Number(rawChannel);
      if (!isNaN(asNum) && asNum > 0) {
        // It's a numeric ID — convert to string
        merged.partnerChannel = String(asNum);
      } else {
        merged.partnerChannel = String(rawChannel);
      }
    } else {
      merged.partnerChannel = "";
    }

    // Normalize invoiceParty: API returns number, MenuItem values are strings
    const rawInvoice = (merged as unknown as Record<string, unknown>)
      .invoiceParty;
    if (rawInvoice !== null && rawInvoice !== undefined && rawInvoice !== "") {
      merged.invoiceParty = Number(rawInvoice); // keep as number; we'll stringify in the Select value prop
    }

    const partnerProgramVal = (merged as unknown as Record<string, unknown>)
      .partnerProgram;
    merged.partnerProgram =
      partnerProgramVal === 0 ? "" : String(partnerProgramVal || "");

    return merged;
  }, [initialValues]);
  

  const { data: titlesResp } = useGetAllTitleConfigurationsList({
    data: {
      pageNumber: "1",
      pageSize: "1000",
      includeInactive: false,
    },
  });
  const titleOptions = titlesResp?.data || [];

  const { data: publishersResp } = useGetAllPublishers({
    data: { pageNumber: "1", pageSize: "100" },
  });

  const { data: invoiceResp } = useGetAllInvoiceParties({
    data: { pageNumber: "1", pageSize: "1000", includeInactive: true },
  });
  const invoiceOptions = invoiceResp?.data ?? [];

  return (
    <CustomDrawer
      open={open}
      onClose={onClose}
      title={title}
      showCloseButton={showCloseButton}
    >
      <Formik
        enableReinitialize
        initialValues={mergedInitialValues}
        validationSchema={PartnerValidationSchema}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Form
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              overflow: "hidden",
              paddingBottom: "80px",
            }}
          >
            <Box
              sx={{
                position: "relative",
                p: 2,
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {PartnerFormFields.map(({ name, label, type }) => {
                // Partner Program select
                if (name === "partnerProgram") {
                  const value = formikProps.values.partnerProgram || "";
                  const isTouched = !!(
                    formikProps.touched &&
                    (formikProps.touched as Record<string, unknown>)[name]
                  );
                  const errVal = (
                    formikProps.errors as Record<string, unknown>
                  )[name];
                  const errMsg =
                    typeof errVal === "string" ? errVal : undefined;
                  return (
                    <TextField
                      key={name}
                      select
                      name={name}
                      label={label}
                      value={value}
                      onChange={(e) => {
                        formikProps.setFieldValue(name, e.target.value);
                      }}
                      fullWidth
                      size="medium"
                      error={!!(isTouched && errMsg)}
                      helperText={isTouched && errMsg ? errMsg : ""}
                    >
                      {programOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }

                // Partner Channel select
                if (name === "partnerChannel") {
                  const value = formikProps.values.partnerChannel || "";
                  const showError = formikProps.submitCount > 0 && !value;
                  return (
                    <TextField
                      key={name}
                      select
                      name={name}
                      label={label}
                      value={value}
                      onChange={(e) => {
                        formikProps.setFieldValue(name, e.target.value, true);
                      }}
                      onBlur={() => {
                        formikProps.setFieldTouched(name, true, true);
                      }}
                      fullWidth
                      size="medium"
                      error={showError}
                      helperText={
                        showError ? "Partner Channel is required" : null
                      }
                      FormHelperTextProps={{
                        sx: {
                          height: showError ? "auto" : "0",
                          margin: 0,
                          padding: showError ? "8px 0 0" : 0,
                          visibility: showError ? "visible" : "hidden",
                        },
                      }}
                      disabled={disable}
                      inputProps={{ "aria-required": "true" }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      SelectProps={{
                        native: false,
                        onInvalid: (e) => e.preventDefault(),
                      }}
                    >
                      <MenuItem value="">Select Partner Channel</MenuItem>
                      {partnerChannels.map((channel) => (
                        <MenuItem
                          key={channel.channelId}
                          value={String(channel.channelId)}
                        >
                          {channel.channelName}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }

                // Partner Title Keys select
                if (name === "partnerTitleKeys") {
                  const selectedValues = formikProps.values.partnerTitleKeys
                    ? formikProps.values.partnerTitleKeys
                        .split(",")
                        .filter(Boolean)
                    : [];
                  const showError =
                    formikProps.submitCount > 0 && selectedValues.length === 0;

                  return (
                    <TextField
                      key={name}
                      select
                      name={name}
                      label="Partner Title Keys"
                      value={selectedValues}
                      onChange={(e) => {
                        const { value } = e.target;
                        formikProps.setFieldValue(
                          name,
                          Array.isArray(value) ? value.join(",") : "",
                          true,
                        );
                      }}
                      onBlur={() => {
                        formikProps.setFieldTouched(name, true, true);
                      }}
                      fullWidth
                      size="medium"
                      error={showError}
                      helperText={
                        showError ? "At least one title must be selected" : null
                      }
                      FormHelperTextProps={{
                        sx: {
                          height: showError ? "auto" : "0",
                          margin: 0,
                          padding: showError ? "8px 0 0" : 0,
                          visibility: showError ? "visible" : "hidden",
                        },
                      }}
                      disabled={disable}
                      inputProps={{ "aria-required": "true" }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      SelectProps={{
                        native: false,
                        multiple: true,
                        renderValue: (selected) => {
                          if (
                            !selected ||
                            (Array.isArray(selected) && selected.length === 0)
                          ) {
                            return <em>Select Title</em>;
                          }
                          return (selected as string[]).join(", ");
                        },
                        onInvalid: (e) => e.preventDefault(),
                      }}
                    >
                      {titleOptions.map((title) => {
                        const isSelected = selectedValues.includes(
                          title.titleName,
                        );
                        return (
                          <MenuItem
                            key={title.id}
                            value={title.titleName}
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentTitles = [...selectedValues];
                              const titleIndex = currentTitles.indexOf(
                                title.titleName,
                              );

                              if (titleIndex === -1) {
                                currentTitles.push(title.titleName);
                              } else {
                                currentTitles.splice(titleIndex, 1);
                              }

                              formikProps.setFieldValue(
                                "partnerTitleKeys",
                                currentTitles.join(","),
                              );
                            }}
                          >
                            <Checkbox checked={isSelected} />
                            <ListItemText
                              primary={`${title.titleName} (${title.titleCode})`}
                            />
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  );
                }

                // Platform select
                if (name === "platform") {
                  const value = String(
                    formikProps.values[name as keyof PartnersFormValues] ?? "",
                  );
                  const isTouched = !!(
                    formikProps.touched &&
                    (formikProps.touched as Record<string, unknown>)[name]
                  );
                  const errVal = (
                    formikProps.errors as Record<string, unknown>
                  )[name];
                  const errMsg =
                    typeof errVal === "string" ? errVal : undefined;

                  return (
                    <TextField
                      key={name}
                      select
                      name={name}
                      label={label}
                      value={value}
                      onChange={formikProps.handleChange}
                      fullWidth
                      size="medium"
                      error={!!(isTouched && errMsg)}
                      helperText={isTouched && errMsg ? errMsg : ""}
                    >
                      {PLATFORM_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }

                // Device select
                if (name === "device") {
                  const value = String(
                    formikProps.values[name as keyof PartnersFormValues] ?? "",
                  );
                  const isTouched = !!(
                    formikProps.touched &&
                    (formikProps.touched as Record<string, unknown>)[name]
                  );
                  const errVal = (
                    formikProps.errors as Record<string, unknown>
                  )[name];
                  const errMsg =
                    typeof errVal === "string" ? errVal : undefined;
                  return (
                    <TextField
                      key={name}
                      select
                      name={name}
                      label={label}
                      value={value}
                      onChange={formikProps.handleChange}
                      fullWidth
                      size="medium"
                      error={!!(isTouched && errMsg)}
                      helperText={isTouched && errMsg ? errMsg : ""}
                    >
                      {DEVICE_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }

                // Invoice Party select
                if (name === "invoiceParty") {
                  const rawInv = formikProps.values.invoiceParty;
                  const value =
                    rawInv !== null && rawInv !== undefined
                      ? String(rawInv)
                      : "";
                  const showError =
                    formikProps.submitCount > 0 &&
                    formikProps.values.invoiceParty === null;

                  return (
                    <TextField
                      key={name}
                      select
                      name={name}
                      label={label}
                      value={value}
                      onChange={(e) => {
                        const raw = e.target.value;
                        formikProps.setFieldValue(
                          name,
                          raw === "" ? null : Number(raw),
                          true,
                        );
                      }}
                      onBlur={() => {
                        formikProps.setFieldTouched(name, true, true);
                      }}
                      fullWidth
                      size="medium"
                      error={showError}
                      helperText={
                        showError ? "Invoice Party is required" : null
                      }
                      FormHelperTextProps={{
                        sx: {
                          height: showError ? "auto" : "0",
                          margin: 0,
                          padding: showError ? "8px 0 0" : 0,
                          visibility: showError ? "visible" : "hidden",
                        },
                      }}
                      disabled={disable}
                      inputProps={{ "aria-required": "true" }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      SelectProps={{
                        native: false,
                        onInvalid: (e) => e.preventDefault(),
                      }}
                    >
                      <MenuItem value="">Select Invoice Party</MenuItem>
                      {invoiceOptions.map((opt) => {
                        const obj = opt as unknown as Record<string, unknown>;
                        const idVal = obj.uid ?? obj.id;
                        return (
                          <MenuItem key={String(idVal)} value={String(idVal)}>
                            {obj.name as string}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  );
                }

                // Publisher select
                if (name === "publisherID") {
                  const value =
                    formikProps.values.publisherID === 0
                      ? ""
                      : String(formikProps.values.publisherID || "");
                  const showError =
                    formikProps.submitCount > 0 &&
                    (formikProps.values.publisherID === null ||
                      formikProps.values.publisherID === undefined ||
                      formikProps.values.publisherID === 0);

                  return (
                    <TextField
                      key={name}
                      select
                      name={name}
                      label="Publisher"
                      value={value}
                      onChange={(e) => {
                        const raw = e.target.value;
                        formikProps.setFieldValue(
                          name,
                          raw === "" ? null : Number(raw),
                          true,
                        );
                      }}
                      onBlur={() => {
                        formikProps.setFieldTouched(name, true, true);
                      }}
                      fullWidth
                      size="medium"
                      error={showError}
                      helperText={showError ? "Publisher is required" : null}
                      FormHelperTextProps={{
                        sx: {
                          height: showError ? "auto" : "0",
                          margin: 0,
                          padding: showError ? "8px 0 0" : 0,
                          visibility: showError ? "visible" : "hidden",
                        },
                      }}
                      disabled={disable}
                      inputProps={{ "aria-required": "true" }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      SelectProps={{
                        native: false,
                        onInvalid: (e) => e.preventDefault(),
                      }}
                    >
                      <MenuItem value="">Select Publisher</MenuItem>
                      {publishersResp?.data?.map((publisher) => (
                        <MenuItem
                          key={String(publisher.id)}
                          value={String(publisher.id)}
                        >
                          {publisher.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }
                return (
                  <FormTextField
                    key={name}
                    type={type}
                    name={name}
                    label={label}
                    value={String(
                      formikProps.values[name as keyof PartnersFormValues],
                    )}
                    onChange={formikProps.handleChange}
                    touched={formikProps.touched as Record<string, unknown>}
                    errors={formikProps.errors as Record<string, unknown>}
                  />
                );
              })}
            </Box>
            <FormFooter
              disable={disable || !formikProps.dirty}
              isSubmitting={formikProps.isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </CustomDrawer>
  );
};

export default PartnerForm;
